import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db, getCollectionName } from './firebase';
import { collection, query, onSnapshot, orderBy, addDoc, deleteDoc, updateDoc, doc } from './firestore-compat';

const COL = 'cfg_tabelas_hc';

const SECOES = [
  { key: 'operadora',       label: 'Operadoras / Convênios', icon: '💳', temRegras: false },
  { key: 'filial',          label: 'Filiais',                 icon: '🏢', temRegras: false },
  { key: 'tipo_remocao',    label: 'Tipos de Remoção',        icon: '🚑', temRegras: true  },
  { key: 'empresa_remocao', label: 'Empresas de Remoção',     icon: '🏭', temRegras: false },
  { key: 'solicitante',     label: 'Solicitantes',            icon: '🙋', temRegras: false },
];

const EMPTY_REG = { exige_cotacao: false, qtd_empresas: 3, prazo_autorizacao_horas: 48 };

// Dados extraídos das listas hardcoded existentes no sistema
// GestaoRemocoes.js: OPERADORAS, TIPOS, PRESTADORES
// GestaoFeridas.js:  FILIAIS
// Captacao.js:       CONVENIOS_DEFAULT
const SEEDS = {
  operadora: [
    // GestaoRemocoes.js → OPERADORAS
    'AMIL', 'BRADESCO', 'PEND. AMIL', 'UNIMED baby', 'Unimed senior',
    'Klini', 'Unimed Brasil', 'Unimed Nova Iguaçu', 'Unimed Leste', 'Unimed Outros',
    // Captacao.js → CONVENIOS_DEFAULT (extras)
    'ASSIM SAÚDE', 'BRADESCO SAÚDE', 'CASSI', 'GEAP', 'GOLDEN CROSS',
    'HAPVIDA', 'NOTREDAME', 'OMINT', 'PETROBRAS', 'PREVENT SENIOR',
    'SULAMERICA', 'UNIMED', 'UNIMED LESTE FLUMINE', 'PARTICULAR', 'OUTRO',
  ].filter((v, i, a) => a.indexOf(v) === i),  // dedup

  filial: [
    // GestaoFeridas.js → FILIAIS
    'Homebaby', 'Vitalcare',
  ],

  tipo_remocao: [
    // GestaoRemocoes.js → TIPOS (com regras padrão; usuário pode editar)
    { valor: 'ELETIVO',          regras: { ...EMPTY_REG } },
    { valor: 'URG. PROGR.',      regras: { ...EMPTY_REG, prazo_autorizacao_horas: 24 } },
    { valor: 'INTERCORRÊNCIA',   regras: { ...EMPTY_REG, prazo_autorizacao_horas: 4  } },
    { valor: 'DESOSPITALIZAÇÃO', regras: { ...EMPTY_REG } },
  ],

  empresa_remocao: [
    // Empresas responsáveis por realizar a remoção
    'Operadora', 'SOS Vida', 'Fly Remoções', 'Removip',
  ],

  solicitante: [
    // GestaoRemocoes.js → SOLICITANTES
    'Núcleo', 'Outro',
  ],
};

export default function TabelasHCAdmin() {
  const [items, setItems]     = useState([]);
  const [open, setOpen]       = useState('operadora');
  const [editId, setEditId]   = useState(null);
  const [editVal, setEditVal] = useState('');
  const [editReg, setEditReg] = useState({ ...EMPTY_REG });
  const [saving, setSaving]   = useState(false);
  const [seeding, setSeeding] = useState(false);
  const seedChecked           = useRef(false);

  const initAddReg = useMemo(() =>
    SECOES.reduce((acc, s) => ({ ...acc, [s.key]: { ...EMPTY_REG } }), {}), []);

  const [addVal, setAddVal] = useState({});
  const [addReg, setAddReg] = useState(initAddReg);

  // Auto-seed: se a tabela estiver vazia na primeira carga, importa os padrões do sistema
  async function seedDefaults() {
    setSeeding(true);
    try {
      for (const { key } of SECOES) {
        const lista = SEEDS[key];
        for (const entry of lista) {
          const { valor, regras } = typeof entry === 'string'
            ? { valor: entry, regras: {} }
            : entry;
          await addDoc(collection(db, getCollectionName(COL)), { tipo: key, valor, regras: regras ?? {} });
        }
      }
    } catch (e) {
      alert('Erro ao importar padrões: ' + (e.message || JSON.stringify(e)));
    } finally {
      setSeeding(false);
    }
  }

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, getCollectionName(COL)), orderBy('valor')),
      snap => {
        const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setItems(loaded);
        if (!seedChecked.current) {
          seedChecked.current = true;
          if (loaded.length === 0) seedDefaults();
        }
      }
    );
    return unsub;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byTipo = useMemo(() => {
    const map = {};
    SECOES.forEach(s => { map[s.key] = []; });
    items.forEach(item => { if (map[item.tipo] !== undefined) map[item.tipo].push(item); });
    return map;
  }, [items]);

  async function handleAdd(key, temRegras) {
    const valor = (addVal[key] || '').trim();
    if (!valor) return;
    setSaving(true);
    try {
      const regras = temRegras ? addReg[key] : {};
      await addDoc(collection(db, getCollectionName(COL)), { tipo: key, valor, regras });
      setAddVal(v => ({ ...v, [key]: '' }));
      if (temRegras) setAddReg(r => ({ ...r, [key]: { ...EMPTY_REG } }));
    } catch (e) {
      alert('Erro ao adicionar: ' + (e.message || JSON.stringify(e)));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Remover este item?')) return;
    try {
      await deleteDoc(doc(db, getCollectionName(COL), id));
    } catch (e) {
      alert('Erro ao remover: ' + (e.message || JSON.stringify(e)));
    }
  }

  async function handleSaveEdit() {
    if (!editVal.trim() || !editId) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, getCollectionName(COL), editId), { valor: editVal.trim(), regras: editReg });
      setEditId(null);
    } catch (e) {
      alert('Erro ao salvar: ' + (e.message || JSON.stringify(e)));
    } finally {
      setSaving(false);
    }
  }

  async function handleReseedTipo(key) {
    const label = SECOES.find(s => s.key === key)?.label || key;
    if (!window.confirm(`Reimportar padrões do sistema para "${label}"? Os itens existentes não serão removidos, apenas novos serão adicionados.`)) return;
    setSaving(true);
    try {
      const existentes = new Set((byTipo[key] || []).map(i => i.valor.toLowerCase()));
      for (const entry of SEEDS[key]) {
        const { valor, regras } = typeof entry === 'string' ? { valor: entry, regras: {} } : entry;
        if (!existentes.has(valor.toLowerCase())) {
          await addDoc(collection(db, getCollectionName(COL)), { tipo: key, valor, regras: regras ?? {} });
        }
      }
    } catch (e) {
      alert('Erro: ' + (e.message || JSON.stringify(e)));
    } finally {
      setSaving(false);
    }
  }

  function startEdit(item) {
    setEditId(item.id);
    setEditVal(item.valor);
    setEditReg({ ...EMPTY_REG, ...(item.regras || {}) });
  }

  const S = {
    card:   { background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 12, overflow: 'hidden' },
    header: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', cursor: 'pointer', background: '#f8f9fa', userSelect: 'none' },
    body:   { padding: '16px 18px' },
    row:    { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f3f4f6', flexWrap: 'wrap' },
    input:  { flex: 1, minWidth: 180, border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px', fontSize: '0.875rem', outline: 'none' },
    btnPri: { background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap' },
    btnDel: { background: 'transparent', color: '#ef4444', border: '1px solid #fca5a5', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.75rem' },
    btnEdt: { background: 'transparent', color: '#4f46e5', border: '1px solid #a5b4fc', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.75rem' },
    btnSec: { background: 'transparent', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.72rem' },
    numIn:  { width: 56, border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 5px', fontSize: '0.8rem', marginLeft: 4 },
  };

  function RegrasForm({ reg, onChange }) {
    return (
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px', marginTop: 8, fontSize: '0.8rem', color: '#475569' }}>
        <strong style={{ display: 'block', marginBottom: 8, color: '#334155' }}>Regras para este tipo:</strong>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <input type="checkbox" checked={!!reg.exige_cotacao} onChange={e => onChange({ ...reg, exige_cotacao: e.target.checked })} />
            Exige cotação
          </label>
          {reg.exige_cotacao && (
            <>
              <label>Nº empresas:<input type="number" min={1} max={10} value={reg.qtd_empresas || 3} onChange={e => onChange({ ...reg, qtd_empresas: +e.target.value })} style={S.numIn} /></label>
              <label>Prazo (h):<input type="number" min={1} value={reg.prazo_autorizacao_horas || 48} onChange={e => onChange({ ...reg, prazo_autorizacao_horas: +e.target.value })} style={{ ...S.numIn, width: 64 }} /></label>
            </>
          )}
        </div>
      </div>
    );
  }

  if (seeding) {
    return (
      <div style={{ padding: '60px 24px', textAlign: 'center', color: '#64748b' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
        <p style={{ fontWeight: 600 }}>Importando dados do sistema...</p>
        <p style={{ fontSize: '0.82rem' }}>Buscando as listas que já existem nas telas do sistema.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: 820 }}>
      <h3 style={{ margin: '0 0 4px', color: '#1a1a2e' }}>Tabelas de Referência — Homecare</h3>
      <p style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: 20 }}>
        Gerencie as listas que alimentam os formulários do sistema. Os dados foram importados automaticamente das telas existentes.
      </p>

      {SECOES.map(({ key, label, icon, temRegras }) => {
        const isOpen = open === key;
        const lista  = byTipo[key] || [];
        const curVal = addVal[key] || '';
        const curReg = addReg[key] || { ...EMPTY_REG };

        return (
          <div key={key} style={S.card}>
            <div style={S.header} onClick={() => setOpen(isOpen ? null : key)}>
              <span style={{ fontSize: '1.1rem' }}>{icon}</span>
              <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', flex: 1 }}>{label}</span>
              <span style={{ color: '#94a3b8', fontSize: '0.8rem', marginRight: 8 }}>{lista.length} {lista.length === 1 ? 'item' : 'itens'}</span>
              <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>{isOpen ? '▲' : '▼'}</span>
            </div>

            {isOpen && (
              <div style={S.body}>
                {lista.length === 0 && (
                  <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: '0 0 12px' }}>Nenhum item cadastrado ainda.</p>
                )}

                {lista.map(item => (
                  <div key={item.id} style={S.row}>
                    {editId === item.id ? (
                      <>
                        <input value={editVal} onChange={e => setEditVal(e.target.value)}
                          style={S.input} onKeyDown={e => e.key === 'Enter' && handleSaveEdit()} autoFocus />
                        <button onClick={handleSaveEdit} style={S.btnPri} disabled={saving}>✓ Salvar</button>
                        <button onClick={() => setEditId(null)} style={{ ...S.btnDel, borderColor: '#d1d5db', color: '#6b7280' }}>✕</button>
                        {temRegras && (
                          <div style={{ flexBasis: '100%' }}>
                            <RegrasForm reg={editReg} onChange={setEditReg} />
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <span style={{ flex: 1, fontSize: '0.88rem', color: '#374151' }}>
                          {item.valor}
                          {temRegras && item.regras?.exige_cotacao && (
                            <span style={{ marginLeft: 8, background: '#fef3c7', color: '#92400e', borderRadius: 10, padding: '1px 9px', fontSize: '0.7rem', fontWeight: 600 }}>
                              Cotação · {item.regras.qtd_empresas || 3} empresa(s) · {item.regras.prazo_autorizacao_horas || 48}h
                            </span>
                          )}
                        </span>
                        <button onClick={() => startEdit(item)} style={S.btnEdt}>✏️</button>
                        <button onClick={() => handleDelete(item.id)} style={S.btnDel}>🗑️</button>
                      </>
                    )}
                  </div>
                ))}

                {/* Formulário de adição */}
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      value={curVal}
                      onChange={e => setAddVal(v => ({ ...v, [key]: e.target.value }))}
                      placeholder={`Novo(a) ${label.toLowerCase()}...`}
                      style={S.input}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAdd(key, temRegras)}
                    />
                    <button onClick={() => handleAdd(key, temRegras)} style={S.btnPri} disabled={saving || !curVal.trim()}>+ Adicionar</button>
                    <button onClick={() => handleReseedTipo(key)} style={S.btnSec} disabled={saving} title="Reimportar padrões do sistema (não remove existentes)">↩ Padrões</button>
                  </div>
                  {temRegras && (
                    <RegrasForm reg={curReg} onChange={reg => setAddReg(r => ({ ...r, [key]: reg }))} />
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div style={{ marginTop: 24, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 18px', fontSize: '0.8rem', color: '#92400e' }}>
        <strong>SQL necessário (rodar 1x no Supabase se ainda não fez):</strong>
        <pre style={{ margin: '8px 0 0', fontFamily: 'monospace', whiteSpace: 'pre-wrap', color: '#78350f', lineHeight: 1.6 }}>{`CREATE TABLE IF NOT EXISTS cfg_tabelas_hc (
  id TEXT PRIMARY KEY,
  data JSONB NOT NULL DEFAULT '{}'::jsonb,
  created_at TIMESTAMPTZ DEFAULT now(),
  updated_at TIMESTAMPTZ DEFAULT now()
);
ALTER TABLE cfg_tabelas_hc ENABLE ROW LEVEL SECURITY;
CREATE POLICY "anon_cfg_tabelas_hc"  ON cfg_tabelas_hc FOR ALL TO anon          USING (true) WITH CHECK (true);
CREATE POLICY "auth_cfg_tabelas_hc"  ON cfg_tabelas_hc FOR ALL TO authenticated USING (true) WITH CHECK (true);
GRANT ALL ON cfg_tabelas_hc TO anon, authenticated;`}</pre>
      </div>
    </div>
  );
}
